<template>
  <div class="signin--wrap" @click.stop="showLanguageList(false)">
    <div class="bg--wrap">
      <div class="background"></div>
      <p class="slogan" v-html="$t(`account.signIn.slogan.first`)"></p>
      <h2 class="logo__beusable">
        <i class="icon icon--logo-new"></i>
      </h2>
    </div>

    <div class="signin__form--wrap">
      <div class="layout-box">
        <div class="container">
          <form @submit.prevent="onSubmit">
            <div class="inner__signin">
              <div class="info--wrap">
                <h1 class="logo__samsung">
                  <i class="icon icon--logo-samsung"></i>
                </h1>
                <strong class="title">{{ $t(`account.signIn.title`) }}</strong>
              </div>

              <ui-input-text :type="'text'"
                             :id="'username'"
                             :name="'user-name'"
                             :modelError="modelError.username"
                             :placeholder="$t('common.id')"
                             :lower="true"
                             @onFocus="modelError.username = ''"
                             @input="getUserName">
                {{ $t(`common.id`) }}
              </ui-input-text>

              <ui-input-text isInputAbove
                             :type="'password'"
                             :id="'password'"
                             :name="'current-password'"
                             :modelError="modelError.password"
                             :placeholder="$t('common.placeHolder.password')"
                             @onFocus="modelError.password = ''"
                             @input="getPassword">
                {{ $t(`common.password`) }}
              </ui-input-text>

              <div class="btn--wrap">
                <UiButton :buttonType="'submit'" :primary="true" class="btn-sign">{{ $t('account.signIn.title') }}</UiButton>
              </div>
            </div>
          </form>
          <div class="data--wrap">
            <button @click="isShowRegisterLayer = true" class="text-data">{{ $t('account.signUp.try.title') }}</button>
            <span class="bar"></span>
            <button class="text-data" @click="isShowResetPasswordLayer = true">{{ $t('account.signIn.form.button.forgotPassword') }}</button>
          </div>
        </div>
        <div class="documentation">
          <p>How to use :
              <a href="https://dream-cdn.beusable.net/dream/files/pdf/SamsungBeusable_guide_20240905.pdf" target="_blank">KO, EN</a></p>
          <p>User Guide v 3.2.1 :
            <a href="https://dream-cdn.beusable.net/dream/files/pdf/%5BGuide%5D_%E1%84%89%E1%85%A1%E1%86%B7%E1%84%89%E1%85%A5%E1%86%BC%E1%84%87%E1%85%B2%E1%84%8C%E1%85%A5%E1%84%87%E1%85%B3%E1%86%AF_%E1%84%89%E1%85%A1%E1%84%8B%E1%85%AD%E1%86%BC%E1%84%8C%E1%85%A1_%E1%84%80%E1%85%A1%E1%84%8B%E1%85%B5%E1%84%83%E1%85%B3_v_3.2.1_(ko).pdf" target="_blank">KO</a> /
            <a href="https://dream-cdn.beusable.net/dream/files/pdf/%5BGuide%5D_SamsungBeusable+Guide_v_3.2.1_(en).pdf" target="_blank">EN</a></p>
        </div>
        <div class="footer--wrap">
          <span class="label">Language</span>
          <div class="language--wrap">
            <span class="current-language" @click.stop.prevent="showLanguageList(true)">
              <i class="icon icon--lang" :class="currentLocale"></i>
              {{ currentLocale.toUpperCase() }}
            </span>
            <i class="icon icon--drop-down"></i>
            <transition name="slide">
              <ul class="language__list" :class="{'is-show': showLanguageListStatus}" v-show="showLanguageListStatus">
                <li v-for="item in localeData" :key="item.code"
                    :class="{ [item.code]: true, on: item.code === currentLocale }"
                    :title="item.title"
                    @click="onLocaleClick(item.code)">
                  <i class="icon icon--lang" :class="item.code"></i> {{ item.code.toUpperCase() }}
                </li>
              </ul>
            </transition>
          </div>
          <p class="copyright" v-html="$t('account.signIn.copyright', { thisYear: thisYear })"></p>
        </div>
      </div>
    </div>

    <div class="dimmed" v-if="useDimmed">
      <reset-password
        v-if="isShowResetPasswordLayer"
        @closeLayer="isShowResetPasswordLayer = false" />
      <reset-password-auth-result
        v-if="isEnteredResetPasswordAuthEmail"
        :authParam="auth"
        @closeLayer="isEnteredResetPasswordAuthEmail = false" />
      <sign-up
        v-if="isShowRegisterLayer"
        @closeLayer="isShowRegisterLayer = false" />
      <sign-up-auth-result
        v-if="isEnteredSignUpAuthEmail"
        :authParam="auth"
        @closeLayer="isEnteredSignUpAuthEmail = false" />
      <auth-invalid-param
        v-if="invalidParam"
        @closeLayer="invalidParam = false" />
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { mapState, mapMutations, mapActions } from 'vuex'
import { setAuthData } from '@/libs/auth-helper'
import { HTTP_STATUS } from '@/libs/constants'
import { checkDupId, signIn } from '@/api/user'
import { changeLocale } from '@/libs/i18n'
import ResetPassword from '@/components/account/ResetPassword'
import ResetPasswordAuthResult from '@/components/account/ResetPasswordAuthResult'
import SignUp from '@/components/account/SignUp'
import SignUpAuthResult from '@/components/account/SignUpAuthResult'
import AuthInvalidParam from '@/components/account/AuthInvalidParam'
import UiButton from '@/components/ui/UiButton'
import UiInputText from '@/components/ui/UiInputText'

export default {
  name: 'SignIn',
  components: {
    ResetPassword,
    ResetPasswordAuthResult,
    SignUp,
    SignUpAuthResult,
    AuthInvalidParam,
    UiButton,
    UiInputText
  },
  data () {
    return {
      model: {
        username: '',
        password: ''
      },
      modelError: {
        username: '',
        password: ''
      },
      auth: {
        key: '',
        value: ''
      },
      invalidParam: false,
      isShowResetPasswordLayer: false,
      isEnteredResetPasswordAuthEmail: false,
      isShowRegisterLayer: false,
      isEnteredSignUpAuthEmail: false,
      showLanguageListStatus: false
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    thisYear () {
      return new Date().getFullYear()
    },
    canSubmit () {
      return this.model.username.length && this.model.password.length
    },
    useDimmed () {
      return this.isShowResetPasswordLayer || this.isShowRegisterLayer || this.isEnteredSignUpAuthEmail || this.isEnteredResetPasswordAuthEmail || this.invalidParam
    },
    currentLocale () {
      return this.$i18n.locale
    }
  },
  methods: {
    ...mapMutations(['setShowStickyData', 'setIsFirstLogin']),
    ...mapActions(['storeUserData']),
    getUserName (value) {
      this.model.username = value
    },
    getPassword (value) {
      this.model.password = value
    },
    getAuthParam () {
      let param = window.location.search
      if (param.indexOf('reset_password') > -1) {
        param = param.split('&')
        param = param[1].split('=')
        this.auth.key = param[0].substring(0, param[0].length)
        this.auth.value = param[1]
      } else {
        param = param.split('=')
        this.auth.key = param[0].substring(1, param[0].length)
        this.auth.value = param[1]
      }

      if (this.auth.value === 'signup_key_expired' || this.auth.value === 'signup_ok') {
        this.isEnteredSignUpAuthEmail = true
      } else if (this.auth.value === 'not_found_reset_key' || this.auth.value === 'reset_key_expired' || this.auth.key === 'key') {
        this.isEnteredResetPasswordAuthEmail = true
      } else if (this.auth.value === 'invalid_param') {
        this.invalidParam = true
      }
    },
    onSubmit () {
      this.modelError.username = ''
      this.modelError.password = ''
      if (this.model.username.length) {
        checkDupId({ username: this.model.username }).then((response) => {
          if (response.okToUse) {
            this.modelError.username = 'notFoundId'
          }
        })
      }
      if (!this.canSubmit) {
        if (!this.model.username.length) {
          this.modelError.username = 'requiredId'
        }
        if (!this.model.password.length) {
          this.modelError.password = 'requiredPassword'
        }
        return
      }
      signIn(this.model).then(data => {
        if (data.firstLogin) {
          this.setIsFirstLogin(true)
        }
        console.log('111:', data)
        setAuthData(data)
        this.$router.replace({ name: 'dashboard' })
      }).catch(err => {
        if (err.message === 'user id not found') {
          this.modelError.username = 'notFoundId'
        }
        if (err.message === 'Unactivated user') {
          this.modelError.username = 'unActivatedId'
        }
        if (err.message === 'No current credit found') {
          this.modelError.username = 'unAbleId'
        }
        if (err.message === 'Unable user') {
          this.modelError.username = 'unApprovedId'
        }
        if (err.message === 'Wrong password') {
          this.modelError.password = 'wrongPassword'
        }
        if (err.response && err.response.status) {
          if (err.response.status === HTTP_STATUS.badRequest) {
            this.modelError.password = 'Check username or password'
          }
        }
      })
    },
    showLanguageList (status) {
      status ? this.showLanguageListStatus = true : this.showLanguageListStatus = false
    },
    onLocaleClick (locale) {
      changeLocale(this.$i18n, locale)
    }
  },
  created () {
    if (this.$route.query.register) {
      this.isShowRegisterLayer = true
    }
    this.localeData = [
      {
        code: 'ja',
        title: 'Japanese'
      },
      {
        code: 'en',
        title: 'English'
      },
      {
        code: 'ko',
        title: 'Korean'
      }
    ]
  },
  mounted () {
    this.getAuthParam()
  },
  beforeRouteEnter (to, from, next) {
    if (store.state.userData) {
      next({ name: 'dashboard' })
    } else {
      next()
    }
  }
}
</script>

<style lang="scss" scoped>
.signin--wrap {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .bg--wrap {
    width: 100%;
    height: 100%;

    .background {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transform: scale(1);
      background-size: cover;
      transition: transform 15s linear, opacity 1.5s linear;
      opacity: 1;
      background: url(../../assets/img/signin/background_img_new.png) lightgray 50%  / cover no-repeat;
      &.scale-up {
        transform: scale(1.2);
      }
      &.fade-in {
        opacity: 1;

        & + .slogan {
          opacity: 1;
        }
      }
      &.fade-out {
        opacity: 0;

        & + .slogan {
          opacity: 0;
        }
      }
    }

    .slogan {
      position: absolute;
      top: 48%;
      left: calc(50% + 174px);
      transform: translate(-50%, -50%);
      font-size: 36px;
      line-height: 1.33;
      text-align: center;
      font-weight: 500;
      color: #fff;
      white-space: pre;
      opacity: 1;
      transition: opacity 1.5s linear;
    }

    .logo__beusable {
      position: fixed;
      top: 20px;
      right: 20px;
    }
  }

  .signin__form--wrap {
    position: fixed;
    top: 7px;
    bottom: 7px;
    left: 8px;
    width: 340px;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #fff;
    overflow: auto;

    .layout-box {
      display: flex;
      flex-direction: column;
      justify-content: center;
      position: relative;
      width: 100%;
      height: 100%;
      min-height: 751px;
      padding: 0 40px;
      box-sizing: border-box;
    }

    .container {
      width: 260px;
    }

    form {
      width: 100%;

      .inner__signin {

        .logo__samsung {
          margin-left: 5px;
        }

        .title {
          display: inline-block;
          margin-top: 10px;
          font-size: 50px;
          color: #555;
          line-height: 52px;
        }

        .desc {
          margin-top: 20px;
          font-size: 13px;
          line-height: 19px;
        }

        .btn_resend {
          text-align: left;
          min-width: 100px;
          height: 32px;
          margin-top: 28px;
          border-radius: 6px;
          background-color: #ebebeb;
          color: #444;
          padding-left: 8px;
          padding-right: 11px;

          .icon--sending {
            vertical-align: middle;
          }

          .btn-text {
            margin-left: 7px;
            font-size: 12px;
            font-weight: bold;
            color: #444444;
          }
        }

        .btn--wrap {
          margin-top: 20px;
          .btn-sign {
            display: block;
            width: 260px;
            height: 38px;
            border-radius: 20px;
            background-color: $brandColor;
            color: #fff;
            font-size: 14px;
            font-weight: bold;
          }
        }

        /deep/ .input--wrap {
          margin-top: 20px;
          margin-bottom: 10px;
        }
      }
    }
  }

  .data--wrap {
    margin-top: 20px;
    text-align: center;

    .text-data {
      font-size: 13px;
      font-weight: 500;
      line-height: 1.46;
      color: #666666;
    }

    .bar {
      content: '';
      display: inline-block;
      width: 1px;
      height: 12px;
      margin: 0 10px;
      background-color: #d7d7d7;
      vertical-align: middle;
    }
  }

  .documentation {
    width: 240px;
    margin-top: 20px;
    padding: 10px;
    font-size: 13px;
    color: #666666;
    text-align: center;

    a {
      text-decoration: underline;
      color: #666666;
      font-weight: 500;
    }

    p {
      margin-bottom: 15px;
    }

    .label {
      margin-right: 11px;
      font-size: 13px;
      color: #0c0c0c;
      font-weight: bold;
    }
  }

  .footer--wrap {
    position: absolute;
    left: 40px;
    bottom: 30px;
    text-align: center;
    width: 260px;

    .label {
      margin-right: 11px;
      font-size: 13px;
      color: #767676;
    }

    .language--wrap {
      display: inline-block;
      position: relative;
      width: 100px;
      height: 38px;
      border-radius: 6px;
      border: solid 1px #bbbbbb;
      background-color: #ffffff;
      box-sizing: border-box;
      text-align: left;

      .current-language,
      .language__list li{
        display: inline-block;
        width: 100%;
        height: 100%;
        line-height: 36px;
        padding-left: 6px;
        box-sizing: border-box;

        font-size: 14px;
        color: #2f2f2f;
        cursor: pointer;

        .icon--lang {
          vertical-align: middle;
          margin-right: 5px;
          position: relative;
          top: -1px;
        }
      }

      .language__list {
        width: 98px;
        border-radius: 6px;
        box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
        background-color: #ffffff;
        overflow: hidden;
        position: absolute;
        top: -1px;
        left: -1px;
        z-index: 1;

        opacity: 0;
        transform-origin: top;
        transition: transform .2s ease, opacity .1s ease;

        &.is-show {
          opacity: 1;
        }

        li:hover {
          background-color: #ebebeb;
        }
      }

      .icon--drop-down {
        pointer-events: none;
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
      }
    }

    .text-legal {
      font-size: 12px;
      line-height: 18px;
      color: #666;
      white-space: nowrap;
    }

    /* language slide */
    .slide-enter, .slide-leave-to{
      transform: scaleY(0);
      opacity: 0;
    }

    .copyright {
      margin: 15px 0 20px 0;
      color: #666;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 12px;
    }
  }

  /deep/ .link-text:hover {
    color: #1199af !important;
  }

  /deep/ .this-year {
    margin-right: 8px;
  }

  /deep/ .mid-dot {
    display: inline-block;
    width: 3px;
    height: 3px;
    vertical-align: middle;
    margin-left: 6px;
    margin-right: 6px;
    background-color: #aaa;
  }
}
</style>
